/**
 * Tiny stale-while-revalidate cache for API GET calls.
 *
 * Why not React Query? We only need instant hydration on return-to-screen —
 * not the whole query-invalidation ecosystem. This is ~40 lines and works
 * without adding a dependency to the bundle.
 *
 * Behaviour:
 *   • On first mount for a key → { data: null, loading: true } then resolves.
 *   • On re-mount (e.g. user tapped Home again) → { data: cached, loading: false }
 *     is returned SYNCHRONOUSLY on the very first render, so the UI paints
 *     instantly, then a background revalidation refreshes it silently.
 *   • Cache survives route changes (module-level Map) and full page reloads
 *     (sessionStorage) inside the same tab.
 *
 * Cache key MUST be a stable string (e.g. `dashboard:2026-02-15`).
 */
import { useEffect, useRef, useState, useCallback } from "react";

const memCache = new Map(); // key -> { data, at }
const inflight = new Map(); // key -> Promise (dedupes simultaneous mounts of the same key)
const FRESH_MS = 2000; // a just-fetched key isn't refetched by another mount within this window

const SS_PREFIX = "swr:";
const SS_MAX_AGE = 5 * 60 * 1000;

function readSession(key) {
  try {
    const raw = sessionStorage.getItem(SS_PREFIX + key);
    if (!raw) return null;
    const { data, at } = JSON.parse(raw);
    if (Date.now() - at > SS_MAX_AGE) return null;
    return data;
  } catch { return null; }
}

function writeSession(key, data) {
  try { sessionStorage.setItem(SS_PREFIX + key, JSON.stringify({ data, at: Date.now() })); } catch {}
}

/**
 * useSWR(key, fetcher)
 *   fetcher: () => Promise<data>
 * Returns: { data, loading, error, refresh, mutate }
 */
export function useSWR(key, fetcher, deps = []) {
  // Read cache SYNCHRONOUSLY on first render so we hydrate instantly.
  const cached = memCache.get(key)?.data ?? readSession(key);
  const [data, setData] = useState(cached);
  const [loading, setLoading] = useState(cached == null);
  const [error, setError] = useState(null);
  // Keep the freshest fetcher without re-triggering the effect on every render.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const run = useCallback(async (force = true) => {
    if (!key) return;
    try {
      const hit = memCache.get(key);
      if (!force && hit && Date.now() - hit.at < FRESH_MS) {
        setData(hit.data);
        return;
      }
      let p = inflight.get(key);
      if (!p) {
        p = fetcherRef.current().finally(() => inflight.delete(key));
        inflight.set(key, p);
      }
      const fresh = await p;
      memCache.set(key, { data: fresh, at: Date.now() });
      writeSession(key, fresh);
      setData(fresh);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    run(false);
  }, [key, run, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps

  const mutate = useCallback((next) => {
    memCache.set(key, { data: next, at: Date.now() });
    writeSession(key, next);
    setData(next);
  }, [key]);

  return { data, loading, error, refresh: useCallback(() => run(true), [run]), mutate };
}

/** Invalidate a cache key so the next mount refetches. Use after mutations. */
export function invalidateSWR(key) {
  memCache.delete(key);
  try { sessionStorage.removeItem(SS_PREFIX + key); } catch {}
}

/** Invalidate any key matching a prefix (e.g. all dashboard dates). */
export function invalidateSWRPrefix(prefix) {
  for (const k of Array.from(memCache.keys())) if (k.startsWith(prefix)) memCache.delete(k);
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(SS_PREFIX + prefix)) sessionStorage.removeItem(k);
    }
  } catch {}
}
